import api, { authService } from './api';

const requestHeaders = () => {
  const token = authService.getToken();
  if (!token) throw new Error('No authentication token');
  return {
    Authorization: `Bearer ${token}`,
  };
};

export const accountsService = {
  getVendorLogin: async (vendorKey, userId) => {
    try {
      const response = await api.get(
        `/accounts/${encodeURIComponent(vendorKey)}/${encodeURIComponent(userId)}/login`,
        { headers: requestHeaders() },
      );
      return { success: true, item: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to load account login',
      };
    }
  },

  listVendorAccounts: async (
    vendorKey,
    {
      includeClosed = false,
      includeIssue = false,
      rtuOnly = false,
      location = '',
      q = '',
      signal,
    } = {},
  ) => {
    try {
      const response = await api.get(`/accounts/${encodeURIComponent(vendorKey)}`, {
        params: {
          include_closed: includeClosed,
          include_issue: includeIssue,
          rtu_only: rtuOnly,
          location: location || undefined,
          q: q || undefined,
        },
        headers: requestHeaders(),
        signal,
      });

      return {
        success: true,
        items: response.data.items || [],
        totalCount: response.data.total_count || 0,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch accounts',
      };
    }
  },

  updateVendorOverride: async (vendorKey, userId, statusOverride) => {
    try {
      const response = await api.patch(`/accounts/${encodeURIComponent(vendorKey)}/${encodeURIComponent(userId)}/override`, {
        status_override: statusOverride,
      }, {
        headers: requestHeaders(),
      });

      return {
        success: true,
        item: response.data.item,
        message: response.data.message,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to update account override',
      };
    }
  },

  updateVendorCardDetails: async (vendorKey, userId, slot, details) => {
    try {
      const response = await api.patch(`/accounts/${encodeURIComponent(vendorKey)}/${encodeURIComponent(userId)}/card-details`, {
        slot,
        details,
      }, {
        headers: requestHeaders(),
      });

      return {
        success: true,
        item: response.data.item,
        message: response.data.message,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to update card details',
      };
    }
  },

  updateVendorLocation: async (vendorKey, userId, location) => {
    try {
      const response = await api.patch(`/accounts/${encodeURIComponent(vendorKey)}/${encodeURIComponent(userId)}/location`, {
        location,
      }, {
        headers: requestHeaders(),
      });

      return {
        success: true,
        item: response.data.item,
        message: response.data.message,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to update account location',
      };
    }
  },

  updateVendorPhoneNumber: async (vendorKey, userId, phoneNumber) => {
    try {
      const response = await api.patch(`/accounts/${encodeURIComponent(vendorKey)}/${encodeURIComponent(userId)}/phone-number`, {
        phone_number: phoneNumber,
      }, {
        headers: requestHeaders(),
      });

      return {
        success: true,
        item: response.data.item,
        message: response.data.message,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to update account phone number',
      };
    }
  },

  updateVendorComment: async (vendorKey, userId, comment) => {
    try {
      const response = await api.patch(`/accounts/${encodeURIComponent(vendorKey)}/${encodeURIComponent(userId)}/comment`, {
        comment,
      }, {
        headers: requestHeaders(),
      });

      return {
        success: true,
        item: response.data.item,
        message: response.data.message,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to update account comment',
      };
    }
  },

  updateTaoApp: async (userId, taoApp) => {
    try {
      const response = await api.patch(`/accounts/tao/${encodeURIComponent(userId)}/tao-app`, {
        tao_app: !!taoApp,
      }, {
        headers: requestHeaders(),
      });

      return {
        success: true,
        item: response.data.item,
        message: response.data.message,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to update Tao App',
      };
    }
  },

  updateTaoPassword: async (userId, password) => {
    try {
      const response = await api.patch(`/accounts/tao/${encodeURIComponent(userId)}/password`, {
        password,
      }, {
        headers: requestHeaders(),
      });

      return {
        success: true,
        item: response.data.item,
        message: response.data.message,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to update Tao password',
      };
    }
  },

  getVendorUserHistory: async (vendorKey, userId) => {
    try {
      const response = await api.get(`/accounts/${encodeURIComponent(vendorKey)}/${encodeURIComponent(userId)}/user-history`, {
        headers: requestHeaders(),
      });

      return {
        success: true,
        ...response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch user history',
      };
    }
  },

  searchCards: async (query, limit = 10) => {
    try {
      const response = await api.get('/cards', {
        params: {
          q: query,
          limit,
        },
        headers: requestHeaders(),
      });

      return {
        success: true,
        items: response.data.items || [],
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to search cards',
      };
    }
  },

  manageVendorCards: async (vendorKey, userId, payload) => {
    try {
      const response = await api.patch(
        `/accounts/${encodeURIComponent(vendorKey)}/${encodeURIComponent(userId)}/cards`,
        payload,
        {
          headers: requestHeaders(),
        }
      );

      return {
        success: true,
        item: response.data.item,
        message: response.data.message,
        confirmationRequired: !!response.data.confirmation_required,
        conflicts: response.data.conflicts || [],
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to manage cards',
      };
    }
  },

  manageVendorPhone: async (vendorKey, userId, payload) => {
    try {
      const response = await api.patch(
        `/accounts/${encodeURIComponent(vendorKey)}/${encodeURIComponent(userId)}/phone`,
        payload,
        {
          headers: requestHeaders(),
        }
      );

      return {
        success: true,
        item: response.data.item,
        message: response.data.message,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to manage phone',
      };
    }
  },

  listDiceAccounts: async ({ includeClosed = false, includeIssue = false, rtuOnly = false, location = '', q = '' } = {}) => {
    try {
      const response = await api.get('/accounts/dice', {
        params: {
          include_closed: includeClosed,
          include_issue: includeIssue,
          rtu_only: rtuOnly,
          location: location || undefined,
          q: q || undefined,
        },
        headers: requestHeaders(),
      });

      return {
        success: true,
        items: response.data.items || [],
        totalCount: response.data.total_count || 0,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch Dice accounts',
      };
    }
  },

  updateDiceOverride: async (userId, statusOverride) => {
    try {
      const response = await api.patch(`/accounts/dice/${encodeURIComponent(userId)}/override`, {
        status_override: statusOverride,
      }, {
        headers: requestHeaders(),
      });

      return {
        success: true,
        item: response.data.item,
        message: response.data.message,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to update Dice override',
      };
    }
  },

  updateDiceCardDetails: async (userId, slot, details) => {
    try {
      const response = await api.patch(`/accounts/dice/${encodeURIComponent(userId)}/card-details`, {
        slot,
        details,
      }, {
        headers: requestHeaders(),
      });

      return {
        success: true,
        item: response.data.item,
        message: response.data.message,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to update card details',
      };
    }
  },

  updateDiceLocation: async (userId, location) => {
    try {
      const response = await api.patch(`/accounts/dice/${encodeURIComponent(userId)}/location`, {
        location,
      }, {
        headers: requestHeaders(),
      });

      return {
        success: true,
        item: response.data.item,
        message: response.data.message,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to update Dice location',
      };
    }
  },

  getDiceUserHistory: async (userId) => {
    try {
      const response = await api.get(`/accounts/dice/${encodeURIComponent(userId)}/user-history`, {
        headers: requestHeaders(),
      });

      return {
        success: true,
        ...response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch user history',
      };
    }
  },
};
